import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Bot,
  TrendingUp,
  Zap,
  CheckCircle,
  Building2,
  BrainCircuit,
  Rocket,
  ShieldCheck,
  HandshakeIcon,
  MessageCircle,
  HeartHandshake,
  Quote,
} from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const AnimatedSection: React.FC<{ children: React.ReactNode; delay?: number }> = ({ children, delay = 0 }) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.08 });
  return (
    <div
      ref={ref}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(32px)',
        transition: `opacity 0.7s cubic-bezier(.22,1,.36,1) ${delay}ms, transform 0.7s cubic-bezier(.22,1,.36,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

const thingsWeHear = [
  {
    quote: 'We know AI could help us, but we have no idea where to start.',
    insight: 'That\'s exactly what we\'re here for. We sit down with your team, learn how your business operates, and identify the 2–3 highest-impact opportunities where AI will deliver measurable results.',
    color: '#8B5CF6',
  },
  {
    quote: 'We can\'t afford to hire an AI team.',
    insight: 'You don\'t need one. You need a partner with the right credentials. We bring 9 professional certifications, a decade of enterprise experience, and production-grade engineering — you bring the knowledge of your business. Together, we build something that lasts.',
    color: '#F59E0B',
  },
  {
    quote: 'What if we invest in this and it doesn\'t work?',
    insight: 'That\'s a smart concern — and it\'s exactly why we start with a focused proof-of-concept using your real data. You see measurable results in weeks before committing further. If the ROI isn\'t there, we\'ll be the first to tell you.',
    color: '#3b82f6',
  },
  {
    quote: 'Our team isn\'t technical. Will they actually use this?',
    insight: 'Every solution we build is designed for the people who use it daily — not for engineers. We provide hands-on training, clear documentation, and ongoing support until your team is fully confident.',
    color: '#22c55e',
  },
];

const howWeWork = [
  {
    step: '01',
    title: 'We Listen First',
    description: 'We start by understanding your business — not pitching a solution. What does your day look like? Where does your team lose the most time? What keeps you up at night? We ask these questions because the best AI solutions come from deeply understanding the problem.',
    icon: <HeartHandshake size={24} />,
    color: '#F59E0B',
  },
  {
    step: '02',
    title: 'We Prove It Before You Pay For It',
    description: 'We build a focused proof-of-concept using your real data — not a polished demo with fake numbers. In 2–4 weeks, you\'ll see exactly what AI can do for your business. If the ROI isn\'t clear, we won\'t push you to move forward.',
    icon: <BrainCircuit size={24} />,
    color: '#8B5CF6',
  },
  {
    step: '03',
    title: 'We Build It Together',
    description: 'This isn\'t a handoff — it\'s a partnership. We develop in short cycles, show you progress every week, and adjust based on your feedback. Your input shapes the solution because nobody knows your business better than you do.',
    icon: <Rocket size={24} />,
    color: '#3b82f6',
  },
  {
    step: '04',
    title: 'We Make Sure Your Team Owns It',
    description: 'We don\'t build something your team can\'t run without us. We train your people, document everything in language they understand, and transition ownership to you. We\'re always a call away — but you\'ll rarely need us.',
    icon: <HandshakeIcon size={24} />,
    color: '#22c55e',
  },
];

const outcomes = [
  'A 5-person accounting team processing invoices in minutes instead of days',
  'A customer support chatbot handling 60% of inquiries overnight — without a single new hire',
  'An operations manager who finally has a dashboard instead of 12 spreadsheets',
  'A marketing team producing a week\'s worth of content in an afternoon',
  'An HR department where new employees can search policies and SOPs instantly',
  'A business owner who sleeps better knowing repetitive work is handled automatically',
];

const whyTrustUs = [
  {
    icon: <ShieldCheck size={28} />,
    title: 'Certified & Battle-Tested',
    description: '9 professional certifications including AWS Solutions Architect, AWS GenAI Developer Professional, and AWS Machine Learning. Our team brings over a decade of hands-on experience from Fortune 500 companies and government agencies.',
    color: '#F59E0B',
  },
  {
    icon: <Building2 size={28} />,
    title: 'Enterprise Quality, Right-Sized Scope',
    description: 'We scope for lean teams and real budgets. Every recommendation is grounded in what will deliver ROI for your business — not what looks impressive on a slide deck.',
    color: '#1E3A5F',
  },
  {
    icon: <TrendingUp size={28} />,
    title: 'Production-Grade from Day One',
    description: 'We don\'t deliver demos that need to be rebuilt for production. Every solution is architected for reliability, security, and scale — the same standards we applied at the enterprise level.',
    color: '#8B5CF6',
  },
  {
    icon: <Zap size={28} />,
    title: 'One Partner, End-to-End',
    description: 'Strategy. Architecture. Development. Cloud. Deployment. Training. Support. One team with the full breadth of expertise — instead of coordinating across multiple vendors.',
    color: '#22c55e',
  },
];

const About: React.FC = () => {
  return (
    <div>
      {/* ─── Hero ─── */}
      <section
        className="hero-fade-in hero-padding-center"
        style={{
          background: 'linear-gradient(135deg, #0F172A 0%, #1E3A5F 50%, #0F172A 100%)',
          borderRadius: '24px',
          marginBottom: '64px',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '300px', height: '300px', borderRadius: '50%', background: 'rgba(245,158,11,0.06)', filter: 'blur(60px)' }} />
        <div style={{ position: 'absolute', bottom: '-40px', left: '-40px', width: '200px', height: '200px', borderRadius: '50%', background: 'rgba(139,92,246,0.06)', filter: 'blur(40px)' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 16px',
              background: 'rgba(245,158,11,0.15)',
              color: '#F59E0B',
              borderRadius: '9999px',
              fontSize: '0.875rem',
              fontWeight: 600,
              marginBottom: '20px',
              border: '1px solid rgba(245,158,11,0.2)',
            }}
          >
            <Bot size={14} /> About FretDev
          </span>
          <h1
            style={{
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              fontWeight: 800,
              fontFamily: 'var(--font-heading)',
              color: '#fff',
              marginBottom: '16px',
              lineHeight: 1.2,
            }}
          >
            Enterprise Experience. <span style={{ color: '#F59E0B' }}>Built for Growing Businesses</span>.
          </h1>
          <p
            style={{
              fontSize: '1.15rem',
              color: 'rgba(255,255,255,0.6)',
              lineHeight: 1.7,
              maxWidth: '640px',
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          >
            Our team spent over a decade building AI and cloud systems at Fortune 500 companies
            and government agencies. Now we bring that same expertise to small and medium businesses —
            because you deserve the same quality of engineering, at a scale and price that makes sense.
          </p>
        </div>
      </section>

      {/* ─── We've Heard It Before ─── */}
      <AnimatedSection>
        <section style={{ marginBottom: '64px' }}>
          <div className="text-center" style={{ marginBottom: '48px' }}>
            <span
              style={{
                display: 'inline-block',
                padding: '6px 16px',
                background: 'rgba(30,58,95,0.08)',
                color: 'var(--color-primary)',
                borderRadius: '9999px',
                fontSize: '0.875rem',
                fontWeight: 600,
                marginBottom: '16px',
              }}
            >
              <MessageCircle size={14} style={{ display: 'inline', verticalAlign: '-2px', marginRight: '6px' }} />
              We've Heard It Before
            </span>
            <h2 className="text-3xl font-bold">These Concerns Are Completely Normal</h2>
            <p style={{ color: 'var(--color-gray-500)', fontSize: '1.05rem', lineHeight: 1.7, maxWidth: '600px', margin: '16px auto 0' }}>
              Every business owner we work with starts in the same place. Here's what they tell us — and what we tell them back.
            </p>
          </div>

          <div className="grid-2-col" style={{ gap: '24px' }}>
            {thingsWeHear.map((item, i) => (
              <AnimatedSection key={i} delay={i * 120}>
                <div
                  style={{
                    background: '#fff',
                    borderRadius: '20px',
                    padding: '32px',
                    border: '1px solid rgba(0,0,0,0.06)',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.03)',
                    transition: 'all 0.3s cubic-bezier(.22,1,.36,1)',
                    height: '100%',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.08)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.03)';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px' }}>
                    <Quote size={20} style={{ color: item.color, flexShrink: 0, marginTop: '2px' }} />
                    <p style={{ fontSize: '1.05rem', fontWeight: 700, fontStyle: 'italic', color: 'var(--color-gray-700)', lineHeight: 1.5 }}>
                      "{item.quote}"
                    </p>
                  </div>
                  <div style={{ borderLeft: `3px solid ${item.color}`, paddingLeft: '16px', marginLeft: '8px' }}>
                    <p style={{ color: 'var(--color-gray-500)', lineHeight: 1.7, fontSize: '0.95rem' }}>
                      {item.insight}
                    </p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </section>
      </AnimatedSection>

      {/* ─── The Reality ─── */}
      <AnimatedSection>
        <section
          className="section-padding-lg"
          style={{
            background: 'linear-gradient(135deg, #0F172A 0%, #1E3A5F 100%)',
            borderRadius: '24px',
            marginBottom: '64px',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '200px', height: '200px', borderRadius: '50%', background: 'rgba(245,158,11,0.08)', filter: 'blur(40px)' }} />
          <div style={{ position: 'absolute', bottom: '-30px', left: '-30px', width: '150px', height: '150px', borderRadius: '50%', background: 'rgba(139,92,246,0.06)', filter: 'blur(30px)' }} />
          <div style={{ position: 'relative', zIndex: 1, maxWidth: '720px', margin: '0 auto', textAlign: 'center' }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 16px',
                background: 'rgba(245,158,11,0.15)',
                color: '#F59E0B',
                borderRadius: '9999px',
                fontSize: '0.875rem',
                fontWeight: 600,
                marginBottom: '20px',
                border: '1px solid rgba(245,158,11,0.2)',
              }}
            >
              Our Perspective
            </span>
            <h2
              style={{
                fontSize: 'clamp(1.5rem, 3vw, 2.2rem)',
                fontWeight: 800,
                color: '#fff',
                marginBottom: '20px',
                fontFamily: 'var(--font-heading)',
                lineHeight: 1.3,
              }}
            >
              AI Isn't Just for Big Companies Anymore
            </h2>
            <p
              style={{
                fontSize: '1.1rem',
                color: 'rgba(255,255,255,0.6)',
                lineHeight: 1.8,
                marginBottom: '16px',
              }}
            >
              The landscape has shifted. The tools have matured, the costs have dropped, and what once required a team of data scientists
              and a six-figure budget can now be delivered at a fraction of that — when you have the right partner.
            </p>
            <p
              style={{
                fontSize: '1.1rem',
                color: 'rgba(255,255,255,0.6)',
                lineHeight: 1.8,
                marginBottom: '16px',
              }}
            >
              The businesses that adopt AI now — even incrementally — will have a significant competitive advantage
              over those that wait. You don't need to understand the engineering under the hood.
              <strong style={{ color: '#F59E0B' }}> You just need a team that does.</strong>
            </p>
            <p
              style={{
                fontSize: '1.1rem',
                color: 'rgba(255,255,255,0.6)',
                lineHeight: 1.8,
              }}
            >
              That’s FretDev. We’re not a vendor who delivers a proposal and disappears.
              We’re a team of engineers who cut their teeth at the enterprise and government level — and now
              bring that same rigor to your business, at your pace, within your budget.
            </p>
          </div>
        </section>
      </AnimatedSection>

      {/* ─── How We Work With You ─── */}
      <AnimatedSection>
        <section style={{ marginBottom: '64px' }}>
          <div className="text-center" style={{ marginBottom: '48px' }}>
            <span
              style={{
                display: 'inline-block',
                padding: '6px 16px',
                background: 'rgba(30,58,95,0.08)',
                color: 'var(--color-primary)',
                borderRadius: '9999px',
                fontSize: '0.875rem',
                fontWeight: 600,
                marginBottom: '16px',
              }}
            >
              Working Together
            </span>
            <h2 className="text-3xl font-bold">How We Partner With You</h2>
            <p style={{ color: 'var(--color-gray-500)', fontSize: '1.05rem', lineHeight: 1.7, maxWidth: '580px', margin: '16px auto 0' }}>
              This isn't a sales process. It's a conversation that starts with your business and ends with your team
              running AI workflows they actually understand.
            </p>
          </div>

          <div className="grid-2-col" style={{ gap: '24px' }}>
            {howWeWork.map((item, i) => (
              <AnimatedSection key={i} delay={i * 120}>
                <div
                  style={{
                    background: '#fff',
                    borderRadius: '20px',
                    padding: '32px',
                    border: '1px solid rgba(0,0,0,0.06)',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.03)',
                    transition: 'all 0.3s cubic-bezier(.22,1,.36,1)',
                    height: '100%',
                    display: 'flex',
                    gap: '20px',
                    alignItems: 'flex-start',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.08)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.03)';
                  }}
                >
                  <div
                    style={{
                      width: '52px',
                      height: '52px',
                      borderRadius: '14px',
                      background: `${item.color}12`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: item.color,
                      flexShrink: 0,
                    }}
                  >
                    {item.icon}
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                      <span style={{ fontSize: '0.8rem', fontWeight: 800, color: item.color, fontFamily: 'var(--font-heading)' }}>{item.step}</span>
                      <h3 style={{ fontSize: '1.1rem', fontWeight: 700 }}>{item.title}</h3>
                    </div>
                    <p style={{ color: 'var(--color-gray-500)', lineHeight: 1.7, fontSize: '0.95rem' }}>{item.description}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </section>
      </AnimatedSection>

      {/* ─── What Changes ─── */}
      <AnimatedSection>
        <section
          className="section-padding-lg"
          style={{
            background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
            borderRadius: '24px',
            marginBottom: '64px',
          }}
        >
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <span
              style={{
                display: 'inline-block',
                padding: '6px 16px',
                background: 'rgba(30,58,95,0.08)',
                color: 'var(--color-primary)',
                borderRadius: '9999px',
                fontSize: '0.875rem',
                fontWeight: 600,
                marginBottom: '16px',
              }}
            >
              Real Outcomes
            </span>
            <h2 className="text-3xl font-bold" style={{ marginBottom: '12px' }}>
              What Actually Changes When You Adopt AI
            </h2>
            <p style={{ color: 'var(--color-gray-500)', fontSize: '1.05rem', lineHeight: 1.7, marginBottom: '32px' }}>
              We don't measure success in buzzwords. We measure it in hours saved, costs cut, and teams that finally have time to focus on what matters.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '28px' }}>
              {outcomes.map((item, i) => (
                <AnimatedSection key={i} delay={i * 60}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <CheckCircle size={20} style={{ color: '#F59E0B', flexShrink: 0, marginTop: '2px' }} />
                    <span style={{ color: 'var(--color-gray-600)', fontSize: '1.05rem', lineHeight: 1.6 }}>{item}</span>
                  </div>
                </AnimatedSection>
              ))}
            </div>
            <Link
              to="/services"
              className="group"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                color: 'var(--color-primary)',
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              See how we make it happen <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </section>
      </AnimatedSection>

      {/* ─── Why Trust Us ─── */}
      <AnimatedSection>
        <section style={{ marginBottom: '64px' }}>
          <div className="text-center" style={{ marginBottom: '48px' }}>
            <span
              style={{
                display: 'inline-block',
                padding: '6px 16px',
                background: 'rgba(30,58,95,0.08)',
                color: 'var(--color-primary)',
                borderRadius: '9999px',
                fontSize: '0.875rem',
                fontWeight: 600,
                marginBottom: '16px',
              }}
            >
              Why FretDev
            </span>
            <h2 className="text-3xl font-bold">Why Businesses Trust FretDev</h2>
          </div>

          <div className="grid-2-col" style={{ gap: '24px' }}>
            {whyTrustUs.map((item, i) => (
              <AnimatedSection key={i} delay={i * 120}>
                <div
                  style={{
                    background: '#fff',
                    borderRadius: '16px',
                    padding: '32px',
                    border: '1px solid rgba(0,0,0,0.06)',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.03)',
                    transition: 'all 0.3s cubic-bezier(.22,1,.36,1)',
                    height: '100%',
                    display: 'flex',
                    gap: '20px',
                    alignItems: 'flex-start',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.08)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.03)';
                  }}
                >
                  <div
                    style={{
                      width: '52px',
                      height: '52px',
                      borderRadius: '14px',
                      background: `${item.color}12`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: item.color,
                      flexShrink: 0,
                    }}
                  >
                    {item.icon}
                  </div>
                  <div>
                    <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '10px' }}>{item.title}</h3>
                    <p style={{ color: 'var(--color-gray-500)', lineHeight: 1.7, fontSize: '0.95rem' }}>{item.description}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </section>
      </AnimatedSection>

      {/* ─── CTA ─── */}
      <AnimatedSection>
        <section
          className="section-padding"
          style={{
            background: 'linear-gradient(135deg, #0F172A 0%, #1E3A5F 100%)',
            borderRadius: '24px',
            marginBottom: '32px',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '200px', height: '200px', borderRadius: '50%', background: 'rgba(245,158,11,0.08)', filter: 'blur(40px)' }} />
          <h2 style={{ fontSize: 'clamp(1.5rem, 3vw, 2.2rem)', fontWeight: 800, color: '#fff', marginBottom: '16px', fontFamily: 'var(--font-heading)', position: 'relative' }}>
            Let's Build Something That Matters
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '1.1rem', marginBottom: '32px', maxWidth: '540px', marginLeft: 'auto', marginRight: 'auto', position: 'relative' }}>
            Our team brings the same engineering standards they applied at Fortune 500 companies
            and government agencies — to every engagement, regardless of company size. Let's talk.
          </p>
          <Link
            to="/contact"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '14px 32px',
              background: 'linear-gradient(135deg, #F59E0B, #D97706)',
              color: '#0F172A',
              borderRadius: '12px',
              fontWeight: 700,
              fontSize: '1rem',
              textDecoration: 'none',
              transition: 'transform 0.25s, box-shadow 0.25s',
              boxShadow: '0 4px 15px rgba(245,158,11,0.35)',
              position: 'relative',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(245,158,11,0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(245,158,11,0.35)';
            }}
          >
            Let's Start a Conversation <ArrowRight size={16} />
          </Link>
        </section>
      </AnimatedSection>
    </div>
  );
};

export default About;
